import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Handshake, ImagePlus, MousePointerClick, Pencil, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton } from "@/components/ui";
import { api, ApiError, type AdminPartner, type PartnerLeadRow, type PartnerSaveInput } from "@/lib/api";
import { fmtDateTime, fmtMoney } from "@/lib/format";
import { msg, t } from "@/lib/i18n";
import { PartnerTile } from "./Partners";

const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? t("Fehler")) : t("Fehler"));
const eur = (v: number | null | undefined) => (v == null ? "—" : fmtMoney(Math.round(v * 100)));
const STATUS: Record<PartnerLeadRow["status"], { label: string; tone: "neutral" | "info" | "success" | "danger" }> = {
  new: { label: msg("Neu"), tone: "info" },
  contacted: { label: msg("In Kontakt"), tone: "neutral" },
  won: { label: msg("Abgeschlossen"), tone: "success" },
  lost: { label: msg("Kein Abschluss"), tone: "danger" },
};
const SIZES: { value: AdminPartner["size"]; label: string }[] = [
  { value: "s", label: msg("Klein (1 Feld)") },
  { value: "m", label: msg("Breit (2 Felder)") },
  { value: "l", label: msg("Groß (2 × 2 Felder)") },
];
const CHANNEL_KINDS = [["shopify", "Shopify"], ["shopware", "Shopware"], ["woocommerce", "WooCommerce"], ["amazon", "Amazon"], ["odoo", "Odoo"], ["manual", "Manuell"], ["sftp", "SFTP"]] as const;

const emptyPartner = (): PartnerSaveInput => ({
  name: "", tagline: "", description: "", category: "Sonstiges", website: null, size: "s", accent: "#1f2937", benefit: "", discountCode: "",
  mode: "lead", linkUrl: null, leadEmail: null, commission: { leadFeeEur: null, percent: null, note: "" }, audience: { channelKinds: [], tenantIds: [] }, active: true, sortOrder: 0,
});

/** Logiful intern → Partner Network: Partner pflegen (Kachel, Vorteil, Provision, Zielgruppe) und Anfragen nachhalten */
export function AdminPartnersPage() {
  const { me } = useTenant();
  const admin = me.user.staffRole === "staff_admin";
  // Partner anlegen/bearbeiten: ganzes Logiful-Team; Löschen und Anfragen nur Admins
  const team = me.user.isStaff;
  const [tab, setTab] = useState<"partners" | "leads">("partners");
  const [editing, setEditing] = useState<AdminPartner | "new" | null>(null);
  const partners = useQuery({ queryKey: ["admin-partners"], queryFn: () => api.partners.adminList() });
  const leads = useQuery({ queryKey: ["admin-partner-leads"], queryFn: () => api.partners.leads() });
  const totals = (leads.data ?? []).reduce((s, l) => ({ n: s.n + 1, won: s.won + (l.status === "won" ? 1 : 0), open: s.open + (l.status === "new" ? 1 : 0), commission: s.commission + l.commissionEur }), { n: 0, won: 0, open: 0, commission: 0 });
  const clicks = (partners.data ?? []).reduce((s, p) => s + p.stats.clicks, 0);

  return (
    <div className="pb-16">
      <PageHeader
        title={t("Partner Network")}
        description={t("Partner-Angebote für alle Kunden: Kachel, Vorteil, Zielgruppe und Provision. Anfragen gehen per Mail an den Partner, hier wird der Abschluss nachgehalten.")}
        actions={team ? <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing("new")}>{t("Partner anlegen")}</Button> : undefined}
      />
      <div className="px-4 md:px-8 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {([
            [t("Partner aktiv"), String((partners.data ?? []).filter((p) => p.active).length)],
            [t("Anfragen"), String(totals.n)],
            [t("Neu, unbearbeitet"), String(totals.open)],
            [t("Abschlüsse"), String(totals.won)],
            [t("Provision gesamt"), eur(totals.commission)],
          ] as const).map(([k, v]) => (
            <Card key={k} className="px-4 py-3"><div className="text-[12px] text-fg-muted">{k}</div><div className="text-[20px] font-semibold tabular-nums">{v}</div></Card>
          ))}
        </div>
        <div className="inline-flex rounded-xl border border-border bg-surface p-1">
          {([["partners", t("Partner")], ["leads", `${t("Anfragen")} ${totals.n ? `(${totals.n})` : ""}`]] as const).map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={cx("h-8 px-3.5 rounded-lg text-[13px] font-medium", tab === k ? "bg-ink text-ink-fg" : "text-fg-muted hover:text-fg")}>{l}</button>
          ))}
        </div>

        {tab === "partners" && (
          <>
            {partners.isLoading && <Skeleton className="h-64 w-full" />}
            {partners.data && !partners.data.length && <EmptyState icon={<Handshake className="size-5" />} title={t("Noch keine Partner")}>{t("Lege den ersten Partner an – er erscheint sofort bei den Kunden.")}</EmptyState>}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[210px] gap-4 [grid-auto-flow:dense]">
              {partners.data?.map((p) => (
                <div key={p.id} className={cx("relative", p.size === "m" && "sm:col-span-2", p.size === "l" && "sm:col-span-2 sm:row-span-2", !p.active && "opacity-50")}>
                  <PartnerTile p={p} onOpen={() => team && setEditing(p)} />
                  <div className="absolute left-3 top-[72px] flex flex-wrap gap-1.5 pointer-events-none">
                    {!p.active && <Badge>{t("inaktiv")}</Badge>}
                    <span className="rounded-full bg-black/45 text-white text-[11px] px-2 py-0.5 backdrop-blur">{p.mode === "lead" ? t("{n} Anfragen · {won} Abschlüsse", { n: p.stats.leads, won: p.stats.won }) : t("{n} Klicks", { n: p.stats.clicks })}{p.stats.commissionEur ? ` · ${eur(p.stats.commissionEur)}` : ""}</span>
                  </div>
                  {team && <span className="absolute right-3 bottom-3 size-8 grid place-items-center rounded-full bg-white text-ink shadow pointer-events-none"><Pencil className="size-3.5" /></span>}
                </div>
              ))}
            </div>
            {!!clicks && <p className="text-[12px] text-fg-muted inline-flex items-center gap-1.5"><MousePointerClick className="size-3.5" />{t("{n} Klicks auf Partner-Links insgesamt", { n: clicks })}</p>}
          </>
        )}

        {tab === "leads" && <LeadsTable rows={leads.data} admin={admin} />}
      </div>
      {editing && <PartnerEditor partner={editing === "new" ? null : editing} canDelete={admin} onClose={() => setEditing(null)} />}
    </div>
  );
}

function LeadsTable({ rows, admin }: { rows: PartnerLeadRow[] | undefined; admin: boolean }) {
  const qc = useQueryClient();
  const upd = useMutation({
    mutationFn: (p: { id: string; status?: PartnerLeadRow["status"]; dealValueEur?: number | null; note?: string }) => api.partners.updateLead(p.id, p),
    onSuccess: (r) => { toast.success(t("Gespeichert"), { description: t("Provision: {v}", { v: eur(r.commissionEur) }) }); void qc.invalidateQueries({ queryKey: ["admin-partner-leads"] }); void qc.invalidateQueries({ queryKey: ["admin-partners"] }); },
    onError: (e) => toast.error(t("Nicht gespeichert"), { description: errMsg(e) }),
  });
  if (!rows) return <Skeleton className="h-64 w-full" />;
  if (!rows.length) return <EmptyState icon={<Handshake className="size-5" />} title={t("Noch keine Anfragen")}>{t("Sobald Kunden einen Partner anfragen, erscheinen die Anfragen hier.")}</EmptyState>;
  return (
    <Card className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead className="text-[12px] text-fg-muted border-b border-border">
          <tr>{[t("Datum"), t("Kunde"), t("Partner"), t("Kontakt"), t("Status"), t("Abschlusswert"), t("Provision"), t("Notiz")].map((h, i) => <th key={i} className={cx("px-3 py-2.5 font-medium text-left whitespace-nowrap", (i === 5 || i === 6) && "text-right")}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((l) => (
            <tr key={l.id} className="border-b border-border last:border-0 align-top">
              <td className="px-3 py-2.5 whitespace-nowrap text-fg-muted">{fmtDateTime(l.createdAt)}{!l.notified && <div><Badge tone="warning">{t("Mail fehlgeschlagen")}</Badge></div>}</td>
              <td className="px-3 py-2.5"><div className="font-medium">{l.tenantName}</div>{l.customerNumber && <div className="text-[11.5px] text-fg-subtle">{t("Kd-Nr.")} {l.customerNumber}</div>}</td>
              <td className="px-3 py-2.5 font-medium">{l.partnerName}</td>
              <td className="px-3 py-2.5 max-w-[260px]">
                <div>{l.contact.name}</div>
                <a href={`mailto:${l.contact.email}`} className="text-[12px] text-fg-muted hover:underline">{l.contact.email}</a>
                {l.message && <div className="mt-1 text-[12px] text-fg-muted line-clamp-3" title={l.message}>„{l.message}“</div>}
              </td>
              <td className="px-3 py-2.5">
                <select disabled={!admin} value={l.status} onChange={(e) => upd.mutate({ id: l.id, status: e.target.value as PartnerLeadRow["status"] })} className="h-8 rounded-lg border border-border bg-surface px-2 text-[12.5px]" aria-label={t("Status")}>
                  {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{t(v.label)}</option>)}
                </select>
              </td>
              <td className="px-3 py-2.5 text-right">
                <Input
                  key={`${l.id}:${l.dealValueEur ?? ""}`}
                  disabled={!admin}
                  defaultValue={l.dealValueEur ?? ""}
                  inputMode="decimal"
                  suffix="€"
                  placeholder="—"
                  aria-label={t("Abschlusswert")}
                  className="h-8 w-28 text-right tabular-nums"
                  onBlur={(e) => { const raw = e.target.value.replace(",", ".").trim(); const v = raw ? Number(raw) : null; if (v !== l.dealValueEur && (v == null || Number.isFinite(v))) upd.mutate({ id: l.id, dealValueEur: v }); }}
                />
              </td>
              <td className="px-3 py-2.5 text-right whitespace-nowrap">
                <div className="font-semibold tabular-nums">{eur(l.commissionEur)}</div>
                <div className="text-[11px] text-fg-subtle">{[l.leadFeeEur ? `${eur(l.leadFeeEur)} ${t("je Anfrage")}` : null, l.percent ? `${l.percent} % ${t("vom Abschluss")}` : null].filter(Boolean).join(" + ") || t("ohne Provision")}</div>
              </td>
              <td className="px-3 py-2.5"><Input key={`${l.id}:n:${l.note}`} disabled={!admin} defaultValue={l.note} placeholder="—" className="h-8 w-44" aria-label={t("Notiz")} onBlur={(e) => { if (e.target.value !== l.note) upd.mutate({ id: l.id, note: e.target.value }); }} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function PartnerEditor({ partner, canDelete, onClose }: { partner: AdminPartner | null; canDelete: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [d, setD] = useState<PartnerSaveInput>(() => {
    if (!partner) return emptyPartner();
    const { id: _i, logoUrl: _l, coverUrl: _c, lastRequestAt: _r, createdAt: _cr, updatedAt: _u, stats: _s, ...rest } = partner;
    return rest;
  });
  const [images, setImages] = useState({ logo: partner?.logoUrl ?? null, cover: partner?.coverUrl ?? null });
  const set = <K extends keyof PartnerSaveInput>(k: K, v: PartnerSaveInput[K]) => setD((x) => ({ ...x, [k]: v }));
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["admin-partners"] }); void qc.invalidateQueries({ queryKey: ["partners"] }); };
  const save = useMutation({
    mutationFn: () => (partner ? api.partners.update(partner.id, d) : api.partners.create(d)),
    onSuccess: () => { toast.success(t("Partner gespeichert")); refresh(); onClose(); },
    onError: (e) => toast.error(t("Nicht gespeichert"), { description: errMsg(e) }),
  });
  const del = useMutation({
    mutationFn: () => api.partners.remove(partner!.id),
    onSuccess: () => { toast.success(t("Partner gelöscht")); refresh(); onClose(); },
    onError: (e) => toast.error(t("Nicht gelöscht"), { description: errMsg(e) }),
  });
  const upload = async (kind: "logo" | "cover", file: File | null) => {
    if (!partner) return toast.info(t("Bitte erst speichern, dann Bilder hochladen."));
    if (file && !["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error(t("Nur PNG, JPEG oder WebP"));
    if (file && file.size > 2 * 1024 * 1024) return toast.error(t("Bild größer als 2 MB"));
    try {
      if (!file) await api.partners.setImage(partner.id, kind, {});
      else {
        const data = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1] ?? ""); r.onerror = rej; r.readAsDataURL(file); });
        await api.partners.setImage(partner.id, kind, { mimeType: file.type, data });
        setImages((x) => ({ ...x, [kind]: `data:${file.type};base64,${data}` }));
      }
      if (!file) setImages((x) => ({ ...x, [kind]: null }));
      refresh();
    } catch (e) {
      toast.error(t("Bild nicht gespeichert"), { description: errMsg(e) });
    }
  };
  const label = "block text-[12px] font-medium text-fg-muted mb-1";
  const num = (v: string) => (v.trim() === "" ? null : Number(v.replace(",", ".")));

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(1000px,calc(100vw-32px))] max-h-[calc(100vh-32px)] overflow-y-auto rounded-2xl border border-border bg-surface p-5 shadow-2xl animate-pop-in">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-[16px] font-semibold">{partner ? t("Partner bearbeiten") : t("Partner anlegen")}</Dialog.Title>
            <Dialog.Close className="size-8 grid place-items-center rounded-lg hover:bg-surface-2" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
          </div>
          <div className="mt-4 grid lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <label><span className={label}>{t("Name")}</span><Input value={d.name} onChange={(e) => set("name", e.target.value)} /></label>
                <label><span className={label}>{t("Kategorie")}</span><Input value={d.category} onChange={(e) => set("category", e.target.value)} list="partner-categories" /></label>
              </div>
              <datalist id="partner-categories">{["Verpackung", "Fotografie", "Finanzierung", "Marketing", "Versicherung", "Software", "Beratung", "Sonstiges"].map((c) => <option key={c} value={c} />)}</datalist>
              <label className="block"><span className={label}>{t("Kurzbeschreibung (Kachel)")}</span><Input value={d.tagline} onChange={(e) => set("tagline", e.target.value)} maxLength={140} /></label>
              <label className="block"><span className={label}>{t("Beschreibung (Detailansicht)")}</span><textarea value={d.description} onChange={(e) => set("description", e.target.value)} rows={4} className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-ink/40" /></label>
              <div className="grid sm:grid-cols-2 gap-3">
                <label><span className={label}>{t("Vorteil für Kunden")}</span><Input value={d.benefit} onChange={(e) => set("benefit", e.target.value)} placeholder={t("z. B. 10 % Rabatt")} maxLength={80} /></label>
                <label><span className={label}>{t("Rabattcode (optional)")}</span><Input value={d.discountCode} onChange={(e) => set("discountCode", e.target.value)} className="font-mono" /></label>
                <label><span className={label}>{t("Website")}</span><Input value={d.website ?? ""} onChange={(e) => set("website", e.target.value || null)} placeholder="https://" /></label>
                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <label><span className={label}>{t("Kachelgröße")}</span>
                    <select value={d.size} onChange={(e) => set("size", e.target.value as AdminPartner["size"])} className="h-9 w-full rounded-lg border border-border bg-surface px-2 text-[13px]">{SIZES.map((s) => <option key={s.value} value={s.value}>{t(s.label)}</option>)}</select>
                  </label>
                  <label><span className={label}>{t("Farbe")}</span><input type="color" value={d.accent} onChange={(e) => set("accent", e.target.value)} className="h-9 w-12 rounded-lg border border-border bg-surface p-1 cursor-pointer" /></label>
                </div>
              </div>

              <fieldset className="rounded-xl border border-border p-3 space-y-3">
                <legend className="px-1 text-[12px] font-semibold">{t("Ablauf")}</legend>
                <div className="flex gap-2">
                  {(["lead", "link"] as const).map((m) => (
                    <button key={m} type="button" onClick={() => set("mode", m)} className={cx("flex-1 rounded-lg border px-3 py-2 text-left", d.mode === m ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2/60")}>
                      <span className="block text-[13px] font-medium">{m === "lead" ? t("Anfrage über Logiful") : t("Direkter Link")}</span>
                      <span className="block text-[11.5px] text-fg-muted">{m === "lead" ? t("Kunde schickt Kontakt + Nachricht, Partner bekommt eine Mail") : t("Kunde springt zum Partner, Klick wird gezählt")}</span>
                    </button>
                  ))}
                </div>
                {d.mode === "lead" ? (
                  <label className="block"><span className={label}>{t("E-Mail für Anfragen")}</span><Input type="email" value={d.leadEmail ?? ""} onChange={(e) => set("leadEmail", e.target.value || null)} /></label>
                ) : (
                  <label className="block"><span className={label}>{t("Link zum Angebot")}</span><Input value={d.linkUrl ?? ""} onChange={(e) => set("linkUrl", e.target.value || null)} placeholder="https://" /></label>
                )}
              </fieldset>

              <fieldset className="rounded-xl border border-border p-3">
                <legend className="px-1 text-[12px] font-semibold">{t("Provision (nur Logiful)")}</legend>
                <div className="grid sm:grid-cols-3 gap-3">
                  <label><span className={label}>{t("Je Anfrage")}</span><Input value={d.commission.leadFeeEur ?? ""} onChange={(e) => set("commission", { ...d.commission, leadFeeEur: num(e.target.value) })} suffix="€" inputMode="decimal" /></label>
                  <label><span className={label}>{t("Vom Abschluss")}</span><Input value={d.commission.percent ?? ""} onChange={(e) => set("commission", { ...d.commission, percent: num(e.target.value) })} suffix="%" inputMode="decimal" /></label>
                  <label><span className={label}>{t("Vereinbarung")}</span><Input value={d.commission.note} onChange={(e) => set("commission", { ...d.commission, note: e.target.value })} placeholder={t("z. B. 12 Monate wiederkehrend")} /></label>
                </div>
              </fieldset>

              <fieldset className="rounded-xl border border-border p-3 space-y-2">
                <legend className="px-1 text-[12px] font-semibold">{t("Zielgruppe")}</legend>
                <p className="text-[12px] text-fg-muted">{t("Leer = alle Kunden. Sonst nur Kunden mit einem dieser Kanäle.")}</p>
                <div className="flex flex-wrap gap-1.5">
                  {CHANNEL_KINDS.map(([k, l]) => {
                    const on = d.audience.channelKinds.includes(k);
                    return <button key={k} type="button" onClick={() => set("audience", { ...d.audience, channelKinds: on ? d.audience.channelKinds.filter((x) => x !== k) : [...d.audience.channelKinds, k] })} className={cx("h-7 rounded-full px-3 text-[12.5px] border", on ? "bg-ink text-ink-fg border-ink" : "border-border hover:bg-surface-2")}>{l}</button>;
                  })}
                </div>
              </fieldset>

              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-[13px] cursor-pointer"><input type="checkbox" checked={d.active} onChange={(e) => set("active", e.target.checked)} className="accent-ink" />{t("Sichtbar für Kunden")}</label>
                <label className="flex items-center gap-2 text-[13px]">{t("Reihenfolge")}<Input type="number" value={d.sortOrder} onChange={(e) => set("sortOrder", Math.round(Number(e.target.value) || 0))} className="h-8 w-20" /></label>
              </div>
            </div>

            {/* Vorschau + Bilder */}
            <div className="space-y-3">
              <p className="text-[12px] font-medium text-fg-muted">{t("Vorschau")}</p>
              <div className={cx("grid", d.size === "l" ? "h-[420px]" : "h-[210px]")}>
                <PartnerTile p={{ id: "preview", ...d, name: d.name || t("Partnername"), logoUrl: images.logo, coverUrl: images.cover }} />
              </div>
              {(["logo", "cover"] as const).map((k) => (
                <div key={k} className="flex items-center gap-2">
                  <label className="flex-1 inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-dashed border-border-strong text-[12.5px] cursor-pointer hover:bg-surface-2">
                    <ImagePlus className="size-4 text-fg-subtle" />
                    {k === "logo" ? t("Logo hochladen (quadratisch)") : t("Titelbild hochladen (quer)")}
                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => void upload(k, e.target.files?.[0] ?? null)} />
                  </label>
                  {images[k] && <button onClick={() => void upload(k, null)} className="size-9 grid place-items-center rounded-lg text-fg-subtle hover:text-danger" aria-label={t("Bild entfernen")}><Trash2 className="size-4" /></button>}
                </div>
              ))}
              {!partner && <p className="text-[11.5px] text-fg-subtle">{t("Bilder nach dem ersten Speichern hochladen.")}</p>}
            </div>
          </div>
          <div className="mt-5 flex items-center justify-between gap-2 border-t border-border pt-4">
            {partner && canDelete ? <Button icon={<Trash2 className="size-4" />} loading={del.isPending} onClick={() => { if (confirm(t("Partner „{name}“ löschen?", { name: partner.name }))) del.mutate(); }}>{t("Löschen")}</Button> : <span />}
            <div className="flex gap-2">
              <Button onClick={onClose}>{t("Abbrechen")}</Button>
              <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
